import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  Grid,
  CardContent,
  CircularProgress,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ArrowBackOutlined, CheckCircleOutline, EastOutlined, MailOutlineOutlined } from "@mui/icons-material";
import * as yup from "yup";
import { ResetPasswordService } from "../../../shared/services/api/reset-password/ResetPasswordService";
import { useNavigate } from "react-router-dom";
import imageSite from "../../../assets/fundo_login.png";

const resetPasswordSchema = yup.object().shape({
  email: yup.string().email().required(),
});

export const ForgotPasswordPresentation: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [email, setEmail] = useState("");
  const [resetCallSuccessful, setResetCallSuccessful] = useState(false);
  const [formError, setFormError] = useState("");

  const navigate = useNavigate();

  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));

  const handleSubmit = async () => {
    setEmailError("");
    setFormError("");
    setIsLoading(true);

    try {
      await resetPasswordSchema.validate({ email }, { abortEarly: false });

      try {
        await ResetPasswordService.save({ email });

        setResetCallSuccessful(true);
        setIsLoading(false);
      } catch (e: any) {
        setIsLoading(false);
        setFormError(e.message || "Nao foi possivel enviar a solicitacao. Tente novamente.");
      }
    } catch (errors: any) {
      setIsLoading(false);

      errors.inner.forEach((error: yup.ValidationError) => {
        if (error.path === "email") {
          setEmailError(error.message);
        }
      });
    }
  };

  const handleNavigateBack = async () => {
    setIsLoading(true);

    navigate("/login");
  };

  return (
      <Grid container height={"100vh"}>
        <Box
          zIndex={1}
          position={"absolute"}
          width={"100vw"}
          height={"100vh"}
          sx={{
            backgroundImage: `url(${imageSite})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box
            sx={{ width: "100%", height: "100%" }}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
          >
            {!resetCallSuccessful ? (
                <Card
                  sx={{
                    borderRadius: "16px",
                    width: "100%",
                    maxWidth: "540px",
                    minHeight: "520px",
                    height: "auto",
                    marginLeft: smDown ? 2 : "20%",
                    marginRight: 2,
                    boxShadow: "0 24px 80px rgba(15, 23, 42, 0.16)",
                  }}
                >
                  <CardContent sx={{ px: 4, py: 4, maxWidth: "460px" }}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={2}
                      width="100%"
                    >
                      <Box
                        width="100%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        mb={1}
                      >
                        <img
                          src="logo_garantti_login.png"
                          alt="Logo"
                          height={"80px"}
                        />
                      </Box>

                      <Typography variant="h5" align="center" color="GrayText" fontWeight={700}>
                        Recuperação de Senha
                      </Typography>

                      <Typography variant="body2" align="center" color="text.secondary">
                        Informe seu e-mail para receber o link de redefinição e concluir a troca da senha com segurança.
                      </Typography>

                      <Paper
                        elevation={0}
                        sx={{
                          mt: 1,
                          p: 2,
                          borderRadius: 3,
                          backgroundColor: "#f8fafc",
                          border: "1px solid #e7edf5",
                        }}
                      >
                        <Box display="flex" alignItems="flex-start" gap={1.5}>
                          <MailOutlineOutlined sx={{ color: "#2563eb", mt: 0.25 }} />
                          <Box>
                            <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                              Como funciona
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Se o e-mail estiver cadastrado, enviaremos uma mensagem com o link para criar uma nova senha.
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>

                      <TextField
                        fullWidth
                        type="email"
                        label="Email"
                        value={email}
                        disabled={isLoading}
                        error={!!emailError}
                        helperText={emailError}
                        onKeyDown={() => setEmailError("")}
                        onChange={(e) => setEmail(e.target.value)}
                        size={"small"}
                        sx={{ mt: 1 }}
                      />

                      {!!formError && (
                        <Alert
                          severity="error"
                          sx={{
                            borderRadius: 3,
                            backgroundColor: "#fef2f2",
                            color: "#991b1b",
                            "& .MuiAlert-icon": {
                              color: "#dc2626",
                            },
                          }}
                        >
                          {formError}
                        </Alert>
                      )}

                      <Alert
                        severity="info"
                        sx={{
                          borderRadius: 3,
                          alignItems: "center",
                          backgroundColor: "#eff6ff",
                          color: "#1e3a8a",
                          "& .MuiAlert-icon": {
                            color: "#2563eb",
                          },
                        }}
                      >
                        Por segurança, sempre valide se a mensagem chegou no e-mail correto antes de abrir o link.
                      </Alert>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ px: 4, pb: 4, pt: 0 }}>
                    <Box
                      display="flex"
                      flexDirection={smDown ? "column-reverse" : "row"}
                      gap={1.5}
                      width="100%"
                    >
                      <Button
                        variant="outlined"
                        disabled={isLoading}
                        onClick={handleNavigateBack}
                        startIcon={<ArrowBackOutlined />}
                        endIcon={
                          isLoading ? (
                            <CircularProgress variant="indeterminate" />
                          ) : undefined
                        }
                        sx={{
                          height: 44,
                          flex: 1,
                          borderColor: "#cbd5e1",
                          color: "#334155",
                        }}
                      >
                        Voltar
                      </Button>

                      <Button
                        variant="contained"
                        disabled={isLoading}
                        onClick={handleSubmit}
                        startIcon={!isLoading ? <EastOutlined /> : undefined}
                        endIcon={
                          isLoading ? (
                            <CircularProgress variant="indeterminate" />
                          ) : undefined
                        }
                        sx={{
                          height: 44,
                          flex: 1.2,
                          background: "#7924c7",
                          boxShadow: "0 14px 24px rgba(121, 36, 199, 0.24)",
                          "&:hover": {
                            background: "#6620aa",
                          },
                        }}
                      >
                        Confirmar
                      </Button>
                    </Box>
                  </CardActions>
                </Card>
            ) : (
                <Card
                  sx={{
                    borderRadius: "16px",
                    width: "100%",
                    maxWidth: "520px",
                    marginLeft: smDown ? 2 : "20%",
                    marginRight: 2,
                    boxShadow: "0 24px 80px rgba(15, 23, 42, 0.16)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <CardContent sx={{ px: 4, py: 5 }}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={2}
                      width="100%"
                      alignItems="center"
                    >
                      <Box
                        width={72}
                        height={72}
                        borderRadius="50%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          backgroundColor: "#ecfdf3",
                          border: "1px solid #bbf7d0",
                        }}
                      >
                        <CheckCircleOutline sx={{ fontSize: 38, color: "#16a34a" }} />
                      </Box>

                      <Typography variant="h5" align="center" fontWeight={700}>
                        Solicitação concluida com sucesso!
                      </Typography>
                      <Typography variant="body2" align="center" color="text.secondary">
                        Se o e-mail informado estiver vinculado a uma conta, o link de redefinição chegará em instantes.
                      </Typography>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ px: 4, pb: 4, pt: 0 }}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="flex-end"
                      width="100%"
                    >
                      <Button
                        variant="contained"
                        disabled={isLoading}
                        onClick={handleNavigateBack}
                        startIcon={!isLoading ? <ArrowBackOutlined /> : undefined}
                        endIcon={
                          isLoading ? (
                            <CircularProgress variant="indeterminate" />
                          ) : undefined
                        }
                        sx={{
                          height: 44,
                          background: "#7924c7",
                          boxShadow: "0 14px 24px rgba(121, 36, 199, 0.24)",
                          "&:hover": {
                            background: "#6620aa",
                          },
                        }}
                      >
                        Ir para o login
                      </Button>
                    </Box>
                  </CardActions>
                </Card>
            )}
          </Box>
        </Box>
      </Grid>
  );
};
